import type {
    BlockStorageErrorCode,
    BlockStorageQuota,
    BlockVolume,
    BlockVolumeStatus,
} from '../apis/v1/block-storage/model';
import type { ProductsPricingResponse } from '../apis/v1/pricing/model';

/** Statuses the backend is still converging from; the list polls faster while any is present. */
export const TRANSITIONAL_STATUSES: ReadonlySet<BlockVolumeStatus> = new Set([
    'creating',
    'attaching',
    'detaching',
    'resizing',
    'deleting',
]);

export const FAST_POLL_MS = 3000;
export const SLOW_POLL_MS = 15000;

export const volumesRefetchInterval = (volumes: readonly Pick<BlockVolume, 'status'>[]) =>
    volumes.some((volume) => TRANSITIONAL_STATUSES.has(volume.status))
        ? FAST_POLL_MS
        : SLOW_POLL_MS;

/** Which operations the volume's status allows; access is checked separately. */
export const allowedActions = ({ status }: Pick<BlockVolume, 'status'>) => ({
    attach: status === 'available',
    detach: status === 'attached',
    resize: status === 'available' || status === 'attached',
    delete: status === 'available' || status === 'error',
});

const TRANSLATED_CODES: ReadonlySet<string> = new Set<BlockStorageErrorCode>([
    'insufficient_credit',
    'block_volume_busy',
    'block_volume_attached',
    'target_not_ready',
    'resource_busy',
    'project_mismatch',
]);

const finite = (value: unknown) =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/** Codes whose text needs numbers from the error: placeholder name to error field. */
const NUMERIC_CODES: ReadonlyMap<string, Readonly<Record<string, string>>> = new Map<
    BlockStorageErrorCode,
    Record<string, string>
>([
    ['block_volume_too_large', { max: 'maxGiB' }],
    ['block_volume_limit', { limit: 'limit' }],
    [
        'block_storage_quota_exceeded',
        { limit: 'limitGiB', used: 'usedGiB', requested: 'requestedGiB' },
    ],
]);

/**
 * Message for a rejected Block storage request: the translated `errors.<code>` for known codes
 * (limit codes only when the error carries their numbers), the server's message for
 * `invalid_request` (it names the field), else undefined so the caller falls back to the generic
 * message.
 */
export function blockStorageErrorText(
    error: unknown,
    translate: (key: string, values?: Record<string, unknown>) => string
): string | undefined {
    if (typeof error !== 'object' || error === null) return undefined;
    const { code, message } = error as { code?: unknown; message?: unknown };
    if (typeof code !== 'string') return undefined;
    if (TRANSLATED_CODES.has(code)) return translate(`errors.${code}`);
    if (code === 'invalid_request' && typeof message === 'string' && message.trim()) {
        return message.trim();
    }
    const fields = error as Record<string, unknown>;
    if (code === 'block_storage_quota_exceeded' && fields.scope === 'platform') {
        return translate('errors.block_storage_platform_capacity');
    }
    const placeholders = NUMERIC_CODES.get(code);
    if (!placeholders) return undefined;
    const values = Object.fromEntries(
        Object.entries(placeholders).map(([name, field]) => [name, finite(fields[field])])
    );
    return Object.values(values).every((value) => value !== undefined)
        ? translate(`errors.${code}`, values)
        : undefined;
}

export const MIN_VOLUME_GIB = 1;

/** Limits assumed when the pricing response omits them. */
export const DEFAULT_BLOCK_STORAGE_LIMITS = {
    max_volume_gib: 100,
    owner_quota_gib: 200,
    max_volumes_per_owner: 20,
};

const limitOr = (value: unknown, fallback: number) => {
    const number = finite(value);
    return number !== undefined && number >= 0 ? number : fallback;
};

/**
 * The owner's limits and usage: the volume list's `quota`, or, from an API that omits it, the
 * pricing limits (else the defaults) with nothing counted as used.
 */
export function resolveQuota(
    quota: BlockStorageQuota | undefined,
    pricing: Partial<ProductsPricingResponse['products']['block_storage']> | undefined
): BlockStorageQuota {
    if (quota) return quota;
    const defaults = DEFAULT_BLOCK_STORAGE_LIMITS;
    return {
        usedGiB: 0,
        limitGiB: limitOr(pricing?.owner_quota_gib, defaults.owner_quota_gib),
        volumes: 0,
        maxVolumes: limitOr(pricing?.max_volumes_per_owner, defaults.max_volumes_per_owner),
        maxVolumeGiB: limitOr(pricing?.max_volume_gib, defaults.max_volume_gib),
    };
}

export interface SizeLimit {
    /**
     * Largest size, in GiB, the request may ask for. Never below `currentGiB`, so with no room left
     * it is 0 on create and the current size on resize; see `createBlockedReason` and
     * `resizeBlockedReason`.
     */
    max: number;
    /** `volume`: the per-volume size cap sets `max`; `quota`: what's left of the owner's GiB quota does. */
    binding: 'volume' | 'quota';
}

/** Largest size for a new volume, or, with `currentGiB`, for growing a volume of that size. */
export function maxVolumeSize(quota: BlockStorageQuota, currentGiB = 0): SizeLimit {
    const quotaMax = currentGiB + quota.limitGiB - quota.usedGiB;
    return currentGiB < quota.maxVolumeGiB && quotaMax < quota.maxVolumeGiB
        ? { max: Math.max(quotaMax, currentGiB), binding: 'quota' }
        : { max: Math.max(quota.maxVolumeGiB, currentGiB), binding: 'volume' };
}

export type VolumeSizeError = 'whole' | 'growOnly' | SizeLimit['binding'];

/**
 * Why `sizeGiB` can't be requested, or null when it can. Pass `currentGiB` on resize, which is
 * grow-only. `volume` and `quota` name the limit the size goes over.
 */
export function volumeSizeError(
    sizeGiB: number,
    limit: SizeLimit,
    currentGiB?: number
): VolumeSizeError | null {
    if (!Number.isInteger(sizeGiB) || sizeGiB < MIN_VOLUME_GIB) return 'whole';
    if (currentGiB !== undefined && sizeGiB <= currentGiB) return 'growOnly';
    return sizeGiB > limit.max ? limit.binding : null;
}

/** Why the owner can't create another volume, or null when they can. */
export function createBlockedReason(
    quota: BlockStorageQuota
): 'volumeLimit' | 'quotaFull' | 'creationPaused' | null {
    if (quota.volumes >= quota.maxVolumes) return 'volumeLimit';
    const { max, binding } = maxVolumeSize(quota);
    if (max >= MIN_VOLUME_GIB) return null;
    return binding === 'volume' ? 'creationPaused' : 'quotaFull';
}

/**
 * Why a volume of `currentGiB` can't grow by at least 1 GiB, or null when it can: `largest` at the
 * per-volume size cap, `quotaFull` when the owner's GiB quota has no room left.
 */
export function resizeBlockedReason(
    quota: BlockStorageQuota,
    currentGiB: number
): 'largest' | 'quotaFull' | 'paused' | null {
    const { max, binding } = maxVolumeSize(quota, currentGiB);
    if (max >= currentGiB + 1) return null;
    if (binding === 'volume') return quota.maxVolumeGiB < MIN_VOLUME_GIB ? 'paused' : 'largest';
    return 'quotaFull';
}

/** Guest commands for a VM attachment, in the order a user runs them. */
export function guestUsageSnippets(devicePath: string, name: string) {
    const mountPoint = `/mnt/${name}`;
    return {
        format: `sudo mkfs.ext4 ${devicePath}`,
        mount: `sudo mkdir -p ${mountPoint} && sudo mount ${devicePath} ${mountPoint}`,
        grow: `sudo resize2fs ${devicePath}`,
        unmount: `sudo umount ${mountPoint}`,
    };
}
